import { useEffect } from 'react';

import dayjs from 'dayjs';

import type { CalendarPosition } from '../CalendarNavigationHandler.types';

type UsePositionBoundsAdjustmentParams = {
  position: CalendarPosition | undefined;
  startDate: string | null;
  endDate: string | null;
  timeSlotMin: Date;
  timeSlotMax: Date;
  timeStep: number;
  setPosition: (position?: CalendarPosition) => void;
};

export const usePositionBoundsAdjustment = ({
  position,
  startDate,
  endDate,
  timeSlotMin,
  timeSlotMax,
  timeStep,
  setPosition
}: UsePositionBoundsAdjustmentParams) => {
  'use memo';
  useEffect(() => {
    if (!position || !startDate || !endDate) return;

    const start = dayjs(startDate);
    const end = dayjs(endDate);

    if (!start.isValid() || !end.isValid()) return;

    const currentWeekDay = position.date.isoWeekday();
    const startWeekDay = start.isoWeekday();

    const dateAdjustedPositionDate = !position.date.isBetween(start, end)
      ? start
          .add(currentWeekDay - startWeekDay, 'days')
          .set('hour', position.date.hour())
          .set('minutes', position.date.minute())
      : position.date;

    const minTime = dayjs(timeSlotMin);
    const maxTime = dayjs(timeSlotMax);

    const minTimeInMinutes = minTime.hour() * 60 + minTime.minute();
    const maxTimeInMinutes = maxTime.hour() * 60 + maxTime.minute();
    const positionDateTimeInMinutes =
      position.date.hour() * 60 + position.date.minute();

    const timeAdjustedPositionDate =
      minTimeInMinutes > positionDateTimeInMinutes
        ? // Position is outside of lower bounds, so we adjust it upwards
          dateAdjustedPositionDate
            .set('hour', minTime.hour())
            .set('minute', minTime.minute())
        : maxTimeInMinutes < positionDateTimeInMinutes
        ? // Position is outside of upper bounds, so we adjust it downwards
          dateAdjustedPositionDate
            .set('hour', maxTime.hour())
            .set('minute', maxTime.minute())
            .subtract(timeStep, 'minutes')
        : dateAdjustedPositionDate;

    if (!timeAdjustedPositionDate.isSame(position.date)) {
      setPosition({ ...position, date: timeAdjustedPositionDate });
    }
  }, [
    startDate,
    endDate,
    timeSlotMin,
    timeSlotMax,
    position,
    timeStep,
    setPosition
  ]);
};
